import React from 'react';
import styled, { keyframes, css, useTheme } from 'styled-components';

/* ============================================================
   Instructor UI primitives — a 1:1 styled-components port of the
   reference panel's component CSS. Every dimension, color, radius,
   shadow and transition is taken verbatim from the reference
   "design tokens & component styles" block. Tokens resolve through
   the ThemeProvider (see theme.js); literal color-mix()/oklch()
   expressions are preserved so output is pixel-identical.

   styled-components scopes each class, so these never collide with
   the marketing-site CSS or with each other.
   ============================================================ */

/* ---------- keyframes ---------- */
export const fade = keyframes`from { opacity: 0; }`;
export const pop = keyframes`from { transform: translateY(12px) scale(.98); opacity: 0; }`;
export const popIn = keyframes`from { opacity: 0; transform: translateY(-6px); }`;
export const routeIn = keyframes`from { transform: translateY(8px); }`;
/* shimmer — verbatim from the design's `ca-shimmer` loading animation */
export const shimmer = keyframes`
  0% { background-position: 0% 0; }
  100% { background-position: -200% 0; }
`;

/* ---------- skeleton / shimmer placeholder ----------
   Drop-in shimmer block for loading states. Props:
   $w (width), $h (height), $r (radius), $circle, $mb (margin-bottom). */
export const Skeleton = styled.div`
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? `linear-gradient(90deg, ${theme.surface3}, color-mix(in oklch, ${theme.surface3} 60%, ${theme.surface}), ${theme.surface3})`
      : `linear-gradient(90deg, ${theme.surface3}, ${theme.surface2}, ${theme.surface3})`};
  background-size: 200% 100%;
  animation: ${shimmer} 1.1s infinite ease-in-out;
  width: ${({ $w }) => $w || '100%'};
  height: ${({ $h }) => $h || '14px'};
  border-radius: ${({ $circle, $r, theme }) => ($circle ? '50%' : $r || theme.rSm)};
  ${({ $circle, $h }) => $circle && css`width: ${$h || '34px'}; flex: none;`}
  margin-bottom: ${({ $mb }) => $mb || '0'};
  display: block;
`;

/* ============================================================
   APP LAYOUT
   ============================================================ */
export const Main = styled.div`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${({ theme }) => theme.bg};
`;

export const Viewport = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
`;

export const Page = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.space} 28px 64px;
`;

/* Admin impersonation pill — compact TopBar chip (not a content-flow banner) */
export const ActAs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 1;
  min-width: 0;
  max-width: min(380px, 40vw);
  height: 32px;
  padding: 0 4px 0 10px;
  border-radius: ${({ theme }) => theme.rPill};
  background: color-mix(in oklch, ${({ theme }) => theme.accent} 10%, ${({ theme }) => theme.surface});
  border: 1px solid color-mix(in oklch, ${({ theme }) => theme.accent} 26%, ${({ theme }) => theme.border});
  font-size: ${({ theme }) => theme.fsMicro};
  line-height: 1;

  .actas-badge {
    flex: none;
    font-weight: 700;
    color: ${({ theme }) => theme.accentStrong};
    white-space: nowrap;
  }

  .actas-sep {
    flex: none;
    color: ${({ theme }) => theme.textSubtle};
  }

  .actas-email {
    flex: 1;
    min-width: 0;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .actas-exit {
    flex: none;
    border: none;
    background: none;
    padding: 5px 10px;
    margin-left: 2px;
    font: inherit;
    font-weight: 650;
    color: ${({ theme }) => theme.accentStrong};
    cursor: pointer;
    border-radius: ${({ theme }) => theme.rSm};
    white-space: nowrap;

    &:hover {
      background: color-mix(in oklch, ${({ theme }) => theme.accent} 12%, transparent);
      text-decoration: underline;
    }
  }

  @media (max-width: 900px) {
    max-width: min(240px, 50vw);

    .actas-badge,
    .actas-sep {
      display: none;
    }
  }
`;

/* ============================================================
   PRIMITIVES
   ============================================================ */
export const Card = styled.div`
  background: ${({ theme }) => theme.surface};
  border-radius: ${({ theme }) => theme.rLg};
  box-shadow: ${({ theme }) => theme.cardShadow};
  border: ${({ theme }) => theme.cardBorder};
  ${({ $pad, theme }) => $pad && css`padding: ${theme.padCard};`}
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin: ${({ theme }) => theme.space} 0 ${({ theme }) => theme.gap};

  h2 {
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 16px;
    font-weight: 700;
    margin: 0;
    letter-spacing: -.01em;
  }
  p {
    margin: 2px 0 0;
    font-size: ${({ theme }) => theme.fsSm};
    color: ${({ theme }) => theme.textMuted};
  }
`;

/* ---------- button ---------- */
export const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  padding: 0 13px;
  border-radius: ${({ theme }) => theme.rMd};
  font-size: 11px;
  font-weight: 650;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid transparent;
  transition: background .15s, border-color .15s, box-shadow .15s, transform .08s;
  text-decoration: none;

  &:active { transform: translateY(1px); }
  svg { width: 15px; height: 15px; }

  /* Default primary uses btnFg so dark-mode ink buttons (light bg) stay readable. */
  color: ${({ theme }) => theme.btnFg};

  /* default = primary (ink) */
  background: ${({ theme }) => theme.btnBg};
  box-shadow: 0 1px 2px rgba(17,20,38,.18), inset 0 1px 0 rgba(255,255,255,.12);
  &:hover {
    background: ${({ theme }) => theme.btnBgHover};
    box-shadow: 0 2px 8px -2px rgba(17,20,38,.4), inset 0 1px 0 rgba(255,255,255,.12);
  }

  ${({ $variant, theme }) => $variant === 'ghost' && css`
    background: ${theme.surface};
    border-color: ${theme.border};
    color: ${theme.text};
    box-shadow: ${theme.shadowSm};
    &:hover { background: ${theme.surface2}; border-color: ${theme.borderStrong}; box-shadow: ${theme.shadowSm}; }
  `}
  ${({ $variant, theme }) => $variant === 'subtle' && css`
    background: ${theme.accent};
    color: #fff;
    box-shadow: none;
    &:hover { background: ${theme.accentStrong}; box-shadow: none; }
  `}
  ${({ $variant, theme }) => $variant === 'danger' && css`
    background: ${theme.danger};
    color: #fff;
    box-shadow: none;
    &:hover { background: color-mix(in oklch, ${theme.danger} 82%, #000); box-shadow: none; }
  `}
  ${({ $sm, theme }) => $sm && css`
    height: 26px;
    padding: 0 10px;
    font-size: ${theme.fsMicro};
    gap: 5px;
    svg { width: 14px; height: 14px; }
  `}
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 11px;
  border-radius: ${({ theme }) => theme.rPill};
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 650;
  white-space: nowrap;
  ${({ $soft, theme }) => $soft && css`
    background: ${theme.surface3};
    color: ${theme.textMuted};
  `}
`;

const tagTone = {
  green: (t) => css`color: ${t.success}; background: color-mix(in oklch, ${t.success} 14%, ${t.surface});`,
  amber: (t) => css`color: oklch(0.58 0.13 60); background: color-mix(in oklch, ${t.warning} 18%, ${t.surface});`,
  gray: (t) => css`color: ${t.textMuted}; background: ${t.surface3};`,
  accent: (t) => css`color: ${t.accentStrong}; background: ${t.accentSoft};`,
  red: (t) => css`color: ${t.danger}; background: color-mix(in oklch, ${t.danger} 13%, ${t.surface});`,
  blue: (t) => css`color: ${t.info}; background: color-mix(in oklch, ${t.info} 14%, ${t.surface});`,
};

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: ${({ theme }) => theme.rPill};
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 700;
  box-shadow: inset 0 0 0 1px color-mix(in oklch, currentColor 20%, transparent);
  ${({ $tone = 'gray', theme }) => (tagTone[$tone] || tagTone.gray)(theme)}
`;

export const TagDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
`;

/**
 * Theme-resolved colors for binary status pills — maps enabled/disabled to design tokens.
 * enabled: forest-green text on pastel green bg
 * disabled: muted text on light gray bg
 */
const STATUS_MAP = {
  enabled: (theme) => ({
    // Light mode uses emerald-700 for screenshot contrast; dark keeps semantic success.
    text: theme.mode === 'light' ? '#047857' : theme.success,
    bg: theme.successSoft,
  }),
  disabled: (theme) => ({
    text: theme.textMuted,
    bg: theme.surface3,
  }),
};

const StatusBadgeRoot = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 100px;
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 650;
  line-height: 1;
  white-space: nowrap;
  color: ${({ $text }) => $text};
  background: ${({ $bg }) => $bg};
`;

/**
 * Text-only pill badge for table enable/disable (and similar binary) status columns.
 * Pastel green / forest-green when $active; light gray / muted when inactive — no dot prefix.
 * Labels come from children (e.g. Enabled/Disabled, Active/Inactive, Approved/Disapproved).
 */
export function StatusBadge({ $active = false, children, ...rest }) {
  const theme = useTheme();
  const colors = ($active ? STATUS_MAP.enabled : STATUS_MAP.disabled)(theme);

  return (
    <StatusBadgeRoot $text={colors.text} $bg={colors.bg} {...rest}>
      {children}
    </StatusBadgeRoot>
  );
}

/* tiles, kpi rows */
export const Tile = styled.div`
  background: ${({ theme }) => theme.surface};
  border-radius: ${({ theme }) => theme.rLg};
  border: ${({ theme }) => theme.cardBorder};
  box-shadow: ${({ theme }) => theme.cardShadow};
  padding: ${({ theme }) => theme.padCard};
`;

export const KpiRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const KpiIco = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  flex: none;
`;

/* segmented control */
export const Seg = styled.div`
  display: inline-flex;
  background: ${({ theme }) => theme.surface3};
  border-radius: ${({ theme }) => theme.rMd};
  padding: 3px;
  gap: 2px;

  button {
    border: none;
    background: none;
    cursor: pointer;
    padding: 0 14px;
    height: 30px;
    border-radius: 9px;
    font-size: ${({ theme }) => theme.fsSm};
    font-weight: 600;
    color: ${({ theme }) => theme.textMuted};
    transition: .15s;
  }
  button.is-on {
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.text};
    box-shadow: ${({ theme }) => theme.shadowSm};
  }
`;

/* ---------- stat cards ---------- */
export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.gap};
  @media (max-width: 980px) { grid-template-columns: repeat(2, 1fr); }
`;

export const Stat = styled.div`
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
  border-radius: ${({ theme }) => theme.rLg};
  box-shadow: ${({ theme }) => theme.cardShadow};
  border: ${({ theme }) => theme.cardBorder};
  padding: ${({ theme }) => theme.padCard};
  transition: transform .18s cubic-bezier(.4,0,.2,1), box-shadow .18s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => `${theme.ring1}, ${theme.shadowLg}`};
  }
`;

export const StatTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const StatIco = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  svg { width: 19px; height: 19px; }

  ${({ $tone, theme }) => {
    const isDark = theme.mode === 'dark';
    if ($tone === 'warning') {
      const mix = isDark ? 22 : 14;
      const line = isDark ? 35 : 22;
      return css`
        background: color-mix(in oklch, ${theme.warning} ${mix}%, ${theme.surface});
        color: ${isDark ? 'oklch(0.78 0.13 60)' : 'oklch(0.58 0.13 60)'};
        box-shadow: inset 0 0 0 1px color-mix(in oklch, ${theme.warning} ${line}%, transparent);
      `;
    }
    if ($tone === 'danger') {
      const mix = isDark ? 20 : 12;
      const line = isDark ? 35 : 22;
      return css`
        background: color-mix(in oklch, ${theme.danger} ${mix}%, ${theme.surface});
        color: ${isDark ? 'oklch(0.72 0.18 25)' : 'oklch(0.59 0.22 25)'};
        box-shadow: inset 0 0 0 1px color-mix(in oklch, ${theme.danger} ${line}%, transparent);
      `;
    }
    return css`
      background: ${theme.accentSoft};
      color: ${theme.accentStrong};
      box-shadow: inset 0 0 0 1px color-mix(in oklch, ${theme.accent} 22%, transparent);
    `;
  }}
`;

export const StatDelta = styled.span`
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px 8px 3px 6px;
  border-radius: ${({ theme }) => theme.rPill};
  ${({ $dir, theme }) => $dir === 'up' && css`
    color: ${theme.success};
    background: color-mix(in oklch, ${theme.success} 14%, ${theme.surface});
  `}
  ${({ $dir, theme }) => $dir === 'down' && css`
    color: ${theme.danger};
    background: color-mix(in oklch, ${theme.danger} 12%, ${theme.surface});
  `}
`;

export const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  margin-top: 16px;
  font-weight: 600;
`;

export const StatValue = styled.div`
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 30px;
  font-weight: 760;
  letter-spacing: -.02em;
  margin-top: 3px;
  line-height: 1;
  small { font-size: 15px; color: ${({ theme }) => theme.textSubtle}; font-weight: 600; }
`;

export const StatBar = styled.div`
  height: 6px;
  border-radius: 4px;
  background: ${({ theme }) => theme.surface3};
  margin-top: 14px;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px rgba(17,20,38,.04);
  > i {
    display: block;
    height: 100%;
    border-radius: 4px;
    background: ${({ theme }) => `linear-gradient(90deg, ${theme.accent}, ${theme.accentStrong})`};
  }
`;

export const StatMeta = styled.div`
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textSubtle};
  margin-top: 7px;
`;

/* ---------- charts ---------- */
export const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: ${({ theme }) => theme.gap};
  @media (max-width: 980px) { grid-template-columns: 1fr; }
`;

export const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: ${({ theme }) => theme.fsSm};
  b { margin-left: auto; font-variant-numeric: tabular-nums; }
`;

export const LegendSw = styled.span`
  width: 11px;
  height: 11px;
  border-radius: 3px;
  flex: none;
  box-shadow: inset 0 0 0 1px rgba(17,20,38,.1);
`;

export const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 14px;
  height: 200px;
  padding-top: 8px;

  .bars__col {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    height: 100%;
    justify-content: flex-end;
  }
  .bars__stack {
    width: 100%;
    max-width: 46px;
    display: flex;
    flex-direction: column-reverse;
    gap: 3px;
    border-radius: 6px 6px 0 0;
    overflow: hidden;
  }
  .bars__seg { width: 100%; }
  .bars__x { font-size: ${({ theme }) => theme.fsMicro}; color: ${({ theme }) => theme.textSubtle}; font-weight: 600; }
`;

/* ---------- table ---------- */
export const TblWrap = styled.div`
  background: ${({ theme }) => theme.surface};
  border-radius: ${({ theme }) => theme.rLg};
  box-shadow: ${({ theme }) => theme.cardShadow};
  border: ${({ theme }) => theme.cardBorder};
  overflow: hidden;
`;

export const TblToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  flex-wrap: wrap;

  .search {
    display: flex;
    align-items: center;
    gap: 8px;
    height: ${({ theme }) => theme.controlH};
    padding: 0 12px;
    background: ${({ theme }) => theme.surface3};
    border-radius: ${({ theme }) => theme.rMd};
    border: 1px solid transparent;
    min-width: 220px;
    flex: 1;
    max-width: 340px;
    color: ${({ theme }) => theme.textMuted};
  }
  .search:focus-within { background: ${({ theme }) => theme.surface}; border-color: ${({ theme }) => theme.accentLine}; }
  .search input { border: none; outline: none; background: none; width: 100%; font-size: 13px; color: ${({ theme }) => theme.text}; }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.fsSm};

  th {
    text-align: left;
    font-size: ${({ theme }) => theme.fsMicro};
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textSubtle};
    padding: 0 16px;
    height: 42px;
    background: ${({ theme }) => theme.surface2};
    border-bottom: 1px solid ${({ theme }) => theme.border};
    position: sticky;
    top: 0;
    z-index: 1;
  }
  td {
    padding: 0 16px;
    height: ${({ theme }) => theme.rowH};
    border-bottom: 1px solid ${({ theme }) => theme.border};
    vertical-align: middle;
  }
  tbody tr { transition: background .12s; }
  tbody tr:hover { background: ${({ theme }) => theme.surface2}; }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr.is-sel { background: ${({ theme }) => `color-mix(in oklch, ${theme.accent} 7%, ${theme.surface})`}; }
  td.muted { color: ${({ theme }) => theme.textMuted}; }
  td.wrap { white-space: normal; word-break: break-word; line-height: 1.45; }
  td.tnum, .tnum { font-variant-numeric: tabular-nums; font-feature-settings: "tnum" 1; }
`;

export const CellPrimary = styled.div`
  font-weight: 650;
  color: ${({ theme }) => theme.text};
`;

export const CellSub = styled.div`
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textSubtle};
  margin-top: 2px;
`;

export const RowIco = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 9px;
  flex: none;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 13px;
  box-shadow: inset 0 0 0 1px rgba(17,20,38,.06);
`;

export const CellFlex = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const Actions = styled.div`
  display: flex;
  gap: 4px;
  opacity: .55;
  transition: opacity .12s;
  tr:hover & { opacity: 1; }
`;

export const Act = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.textMuted};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.surface3}; color: ${({ theme }) => theme.text}; }
  &:hover.danger { color: ${({ theme }) => theme.danger}; }
`;

/* progress mini */
export const MiniProg = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;

  .track {
    width: 84px;
    height: 6px;
    border-radius: 4px;
    background: ${({ theme }) => theme.surface3};
    overflow: hidden;
    box-shadow: inset 0 0 0 1px rgba(17,20,38,.05);
  }
  .track > i { display: block; height: 100%; border-radius: 4px; background: ${({ theme }) => theme.accent}; }
  span {
    font-size: ${({ theme }) => theme.fsMicro};
    color: ${({ theme }) => theme.textMuted};
    font-variant-numeric: tabular-nums;
    min-width: 30px;
  }
`;

/* avatar */
export const Av = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.rPill};
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  flex: none;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.18), 0 1px 2px rgba(17,20,38,.18);
`;

/* ---------- assessment cards (today) ---------- */
export const AssessRail = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(248px, 1fr));
  gap: ${({ theme }) => theme.gap};
`;

export const Acard = styled.div`
  background: ${({ theme }) => theme.surface};
  border-radius: ${({ theme }) => theme.rLg};
  border: ${({ theme }) => theme.cardBorder};
  box-shadow: ${({ theme }) => theme.cardShadow};
  padding: ${({ theme }) => theme.padCard};
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
  transition: transform .15s, box-shadow .15s;
  &:hover { transform: translateY(-3px); box-shadow: ${({ theme }) => theme.shadowLg}; }

  .acard__top { display: flex; align-items: center; justify-content: space-between; }
  .acard__course { font-size: ${({ theme }) => theme.fsMicro}; color: ${({ theme }) => theme.textSubtle}; font-weight: 650; text-transform: uppercase; letter-spacing: .04em; }
  .acard__name { font-family: ${({ theme }) => theme.fontHead}; font-weight: 700; font-size: 15px; letter-spacing: -.01em; }
  .acard__meta { display: flex; gap: 16px; font-size: ${({ theme }) => theme.fsMicro}; color: ${({ theme }) => theme.textMuted}; }
  .acard__meta b { color: ${({ theme }) => theme.text}; font-weight: 650; display: block; margin-top: 2px; font-size: ${({ theme }) => theme.fsSm}; }
  .acard__foot { display: flex; align-items: center; justify-content: space-between; padding-top: 12px; border-top: 1px solid ${({ theme }) => theme.border}; }
`;

/* ---------- modal ---------- */
/** Above impersonation frame (1250) and sidebar mobile toggle (1300). */
export const MODAL_SCRIM_Z_INDEX = 1400;

export const Scrim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(8,10,20,.5);
  backdrop-filter: blur(3px);
  z-index: ${MODAL_SCRIM_Z_INDEX};
  display: grid;
  place-items: center;
  padding: 24px;
  animation: ${fade} .18s ease;
`;

export const Modal = styled.div`
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  border-radius: ${({ theme }) => theme.rXl};
  box-shadow: ${({ theme }) => theme.shadowLg};
  width: 100%;
  max-width: 720px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${pop} .2s cubic-bezier(.34,1.4,.5,1);
  border: 1px solid ${({ theme }) => theme.border};
  box-sizing: border-box;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  .modal__head {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 22px 24px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
  }
  .modal__head h3 {
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 18px;
    font-weight: 750;
    margin: 0;
    letter-spacing: -.01em;
    color: ${({ theme }) => theme.text};
  }
  .modal__head p { margin: 3px 0 0; font-size: ${({ theme }) => theme.fsSm}; color: ${({ theme }) => theme.textMuted}; }
  .modal__body { padding: 22px 24px; overflow-y: auto; }
  .modal__foot { padding: 16px 24px; border-top: 1px solid ${({ theme }) => theme.border}; display: flex; gap: 10px; justify-content: flex-end; background: ${({ theme }) => theme.surface2}; }
  .x-btn { margin-left: auto; color: ${({ theme }) => theme.textMuted}; }
`;

/* form */
export const Field = styled.div`
  margin-bottom: 16px;
  > label { display: block; font-size: ${({ theme }) => theme.fsSm}; font-weight: 650; margin-bottom: 6px; color: ${({ theme }) => theme.text}; }
`;

const controlStyles = css`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  height: ${({ theme }) => theme.controlH};
  padding: 0 13px;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface2};
  color: ${({ theme }) => theme.text};
  font-size: 13.5px;
  outline: none;
  transition: .15s;
  box-sizing: border-box;
  &:focus {
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surface};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.accentSoft};
  }
`;

export const Input = styled.input`${controlStyles}`;
export const Select = styled.select`${controlStyles}`;
export const Textarea = styled.textarea`
  ${controlStyles}
  height: auto;
  padding: 11px 13px;
  resize: vertical;
  min-height: 90px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
`;

/* question review */
export const QItem = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  padding: 16px;
  margin-bottom: 12px;
  background: ${({ theme }) => theme.surface2};

  .q-head { display: flex; gap: 12px; align-items: flex-start; }
  .q-num { width: 26px; height: 26px; flex: none; border-radius: 8px; background: ${({ theme }) => theme.accentSoft}; color: ${({ theme }) => theme.accentStrong}; display: grid; place-items: center; font-size: 12px; font-weight: 800; }
  .q-text { font-weight: 600; font-size: 14px; line-height: 1.5; }
  .q-opts { display: grid; gap: 8px; margin: 12px 0 0 38px; }
  .q-opt { display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: ${({ theme }) => theme.rSm}; border: 1px solid ${({ theme }) => theme.border}; font-size: 13px; background: ${({ theme }) => theme.surface}; }
  .q-opt.correct { border-color: ${({ theme }) => theme.success}; background: ${({ theme }) => `color-mix(in oklch, ${theme.success} 10%, ${theme.surface})`}; color: ${({ theme }) => theme.text}; font-weight: 600; }
  .q-opt .opt-key { width: 22px; height: 22px; border-radius: 6px; background: ${({ theme }) => theme.surface3}; display: grid; place-items: center; font-size: 11px; font-weight: 700; flex: none; }
  .q-opt.correct .opt-key { background: ${({ theme }) => theme.success}; color: #fff; }
`;

/* empty / utility */
export const Muted = styled.span`color: ${({ theme }) => theme.textMuted};`;
export const RowBetween = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 16px;`;
export const Grid2 = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: ${({ theme }) => theme.gap};
  @media (max-width: 980px) { grid-template-columns: 1fr; }
`;
export const Grid3 = styled.div`
  display: grid; grid-template-columns: repeat(3,1fr); gap: ${({ theme }) => theme.gap};
  @media (max-width: 980px) { grid-template-columns: 1fr; }
`;

/* settings */
export const SetTabs = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  margin-bottom: ${({ theme }) => theme.space};
`;

export const SetTab = styled.div`
  padding: 0 4px 12px;
  margin-right: 18px;
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 650;
  color: ${({ theme }) => theme.textMuted};
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  ${({ $on, theme }) => $on && css`color: ${theme.accentStrong}; border-bottom-color: ${theme.accent};`}
`;

export const SetRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 0;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  &:last-child { border-bottom: none; }

  .set-row__txt h4 { margin: 0; font-size: 14px; font-weight: 650; }
  .set-row__txt p { margin: 3px 0 0; font-size: ${({ theme }) => theme.fsSm}; color: ${({ theme }) => theme.textMuted}; max-width: 52ch; }
`;

/* switch */
export const Switch = styled.div`
  width: 42px;
  height: 24px;
  border-radius: 999px;
  background: ${({ theme }) => theme.surface3};
  border: 1px solid ${({ theme }) => theme.border};
  position: relative;
  cursor: pointer;
  flex: none;
  transition: .18s;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: ${({ theme }) => theme.shadowSm};
    transition: .18s;
  }
  ${({ $on, theme }) => $on && css`
    background: ${theme.accent};
    border-color: ${theme.accent};
    &::after { left: 20px; }
  `}
`;

/* row checkbox / bulk select */
export const Cbx = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid ${({ theme }) => theme.borderStrong};
  background-color: ${({ theme }) => theme.surface};
  cursor: pointer;
  display: grid;
  place-items: center;
  flex: none;
  transition: .12s;
  color: transparent;
  svg { width: 13px; height: 13px; }
  ${({ $on, theme }) => $on && css`
    background-color: ${theme.accent} !important;
    border-color: ${theme.accent} !important;
    color: ${theme.onAccent};
  `}
`;

export const Bulkbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  background: ${({ theme }) => theme.accentSoft};
  border-bottom: 1px solid ${({ theme }) => theme.accentLine};
  font-size: ${({ theme }) => theme.fsSm};
  b { font-weight: 700; }
`;

/* icon button (topbar) */
export const IconBtn = styled.button`
  width: ${({ theme }) => theme.controlH};
  height: ${({ theme }) => theme.controlH};
  flex: none;
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textMuted};
  cursor: pointer;
  transition: .15s;
  position: relative;
  box-shadow: ${({ theme }) => theme.shadowSm};

  &:hover, &.is-open { color: ${({ theme }) => theme.text}; background: ${({ theme }) => theme.surface2}; border-color: ${({ theme }) => theme.borderStrong}; }
  .dot { position: absolute; top: 8px; right: 9px; width: 7px; height: 7px; border-radius: 50%; background: ${({ theme }) => theme.danger}; border: 2px solid ${({ theme }) => theme.surface}; }
`;

/* topbar popovers */
export const Pop = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  min-width: 240px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rLg};
  box-shadow: ${({ theme }) => theme.shadowLg};
  padding: 6px;
  z-index: 40;
  animation: ${popIn} .15s cubic-bezier(.2,.7,.3,1);
  max-height: 70vh;
  overflow-y: auto;
  ${({ $wide }) => $wide && css`width: 360px; max-width: 80vw;`}

  .pop__grp { font-size: ${({ theme }) => theme.fsMicro}; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: ${({ theme }) => theme.textSubtle}; padding: 10px 10px 4px; }
  .pop__row { display: flex; align-items: center; gap: 11px; padding: 9px 10px; border-radius: ${({ theme }) => theme.rMd}; cursor: pointer; transition: background .12s; }
  .pop__row:hover { background: ${({ theme }) => theme.surface2}; }
  .pop__row.is-unread { background: ${({ theme }) => `color-mix(in oklch, ${theme.accent} 5%, ${theme.surface})`}; }
  .pop__empty { padding: 22px 14px; text-align: center; color: ${({ theme }) => theme.textMuted}; font-size: ${({ theme }) => theme.fsSm}; }
  .pop__head { display: flex; align-items: center; justify-content: space-between; padding: 8px 10px 10px; border-bottom: 1px solid ${({ theme }) => theme.border}; margin-bottom: 4px; font-size: 14px; }
  .pop__link { border: none; background: none; color: ${({ theme }) => theme.accentStrong}; font-weight: 650; font-size: ${({ theme }) => theme.fsSm}; cursor: pointer; }
  .pop__foot { text-align: center; padding: 10px; margin-top: 4px; border-top: 1px solid ${({ theme }) => theme.border}; color: ${({ theme }) => theme.accentStrong}; font-weight: 650; font-size: ${({ theme }) => theme.fsSm}; cursor: pointer; }
  .pop__foot:hover { background: ${({ theme }) => theme.surface2}; border-radius: ${({ theme }) => theme.rMd}; }
`;

/* catalog cards */
export const CatalogStats = styled.div`
  display: flex;
  gap: 28px;
  margin: 18px 0;
  > div { display: flex; flex-direction: column; gap: 5px; }
  span { font-size: ${({ theme }) => theme.fsMicro}; color: ${({ theme }) => theme.textSubtle}; font-weight: 600; }
  b { font-size: 16px; font-weight: 700; font-family: ${({ theme }) => theme.fontHead}; }
`;

export const CatalogFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 18px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;
